import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OrderStatusBadge, PaymentBadge, MethodBadge } from "@/components/Badges";
import { OrderItemRow } from "@/components/OrderItems";
import { DosingCalculator } from "@/components/DosingCalculator";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money, fmtDateTime, parseMg } from "@/lib/api";

function useReorder() {
  const { addLine } = useCart();
  const nav = useNavigate();
  return async (o) => {
    try {
      const { data } = await api.get("/shop/products");
      const skipped = [];
      let added = 0;
      o.items.forEach((it) => {
        const p = data.find((x) => x.id === it.product_id);
        if (!p || p.stock_status === "out_of_stock") return skipped.push(`${it.name} ${it.vial_size || ""}`.trim());
        addLine(p, {
          quantity: it.quantity, reconstitute: !!it.reconstitute, bac_option: it.bac_option || "recommended",
          custom_bac_ml: it.bac_option === "custom" ? String(it.bac_ml) : "",
        });
        added += 1;
      });
      if (skipped.length) toast.warning(`Not available right now: ${skipped.join(", ")}`);
      if (added) {
        toast.success("Added to your cart with the same options");
        nav("/cart");
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
}

export default function MyOrders() {
  const [orders, setOrders] = useState(null);
  const [dose, setDose] = useState(null);
  const reorder = useReorder();

  useEffect(() => {
    api.get("/my/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14">
      <p className="eyebrow mb-3">History</p>
      <h1 className="mb-8 text-4xl font-bold">My orders</h1>
      {orders && orders.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-10 text-center" data-testid="my-orders-empty">
          <p className="text-muted-foreground">No orders yet.</p>
          <Button asChild className="mt-4"><Link to="/shop">Start shopping</Link></Button>
        </div>
      )}
      <div className="space-y-5">
        {(orders || []).map((o, i) => (
          <div key={o.id} className="rounded-lg border border-border bg-card p-6 rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`my-order-${o.order_number}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono-data text-lg">{o.order_number}</p>
                <p className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <OrderStatusBadge status={o.status} testId={`my-order-status-${o.order_number}`} />
                <MethodBadge method={o.payment_method} />
                <PaymentBadge status={o.payment_status} />
              </div>
            </div>
            {o.expected_ready_label && ["pending", "confirmed"].includes(o.status) && (
              <p className="mb-2 font-mono-data text-sm text-sky-400" data-testid={`my-order-expected-ready-${o.order_number}`}>Expected ready: {o.expected_ready_label}</p>
            )}
            {o.items.map((it, idx) => (
              <OrderItemRow key={idx} it={it} action={it.reconstitute ? (
                <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => setDose(it)} data-testid={`my-order-dosing-${o.order_number}-${idx}`}>
                  <Calculator className="h-3.5 w-3.5" />Calculator
                </Button>
              ) : null} />
            ))}
            {(o.messages || []).length > 0 && (
              <div className="mt-4 space-y-2" data-testid={`my-order-messages-${o.order_number}`}>
                <p className="eyebrow">Messages from Korevex Bio</p>
                {o.messages.map((m) => (
                  <div key={m.id} className="border-l-2 border-emerald-500 pl-3">
                    <p className="whitespace-pre-wrap text-sm">{m.text}</p>
                    <p className="font-mono-data text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center justify-end gap-6 font-mono-data text-sm">
              {o.discount > 0 && <span className="text-emerald-400" data-testid={`my-order-discount-${o.order_number}`}>{o.promo_code || o.discount_label} −{money(o.discount)}</span>}
              {o.recon_fees > 0 && <span className="text-muted-foreground">Recon fees {money(o.recon_fees)}</span>}
              <span>Total <span className="text-emerald-400">{money(o.total)}</span></span>
              <Button size="sm" variant="outline" className="gap-1.5 font-sans" onClick={() => reorder(o)} data-testid={`my-order-reorder-${o.order_number}`}>
                <RotateCcw className="h-3.5 w-3.5" />Order again
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!dose} onOpenChange={(v) => !v && setDose(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="dosing-dialog">
          <DialogHeader><DialogTitle>Reconstitution calculator: {dose?.name} {dose?.vial_size}</DialogTitle></DialogHeader>
          {dose && <DosingCalculator initial={{ mg: parseMg(dose.vial_size) ?? "", ml: dose.bac_ml }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
